import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useInputFocus } from '../hooks/useInputFocus';
import { X, Plus, Calendar, Clock, Users, FileText, ChevronDown, Search, Check } from 'lucide-react';
import { useClients } from '../hooks/useClients';
import { useWorkouts } from '../hooks/useWorkouts';
import { WorkoutCreate, Client } from '../types';

// Схема валидации для создания тренировки
const createWorkoutSchema = z.object({
  name: z.string().min(1, 'Название тренировки обязательно'),
  description: z.string().optional(),
  date: z.string().min(1, 'Дата обязательна'),
  time: z.string().min(1, 'Время обязательно'),
  duration_minutes: z.number().min(1, 'Длительность должна быть больше 0').max(480, 'Длительность не может быть больше 8 часов'),
  notes: z.string().optional(),
  client_ids: z.array(z.number()).min(1, 'Выберите хотя бы одного клиента'),
});

type CreateWorkoutFormData = z.infer<typeof createWorkoutSchema>;

interface CreateWorkoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: WorkoutCreate) => void;
  isLoading?: boolean;
  preSelectedClient?: Client; // Предзаполненный клиент
  preSelectedDate?: Date; // Предустановленная дата
}

const CreateWorkoutModal: React.FC<CreateWorkoutModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  preSelectedClient,
  preSelectedDate,
}) => {
  const { clients, isLoading: isClientsLoading } = useClients();
  const { workouts } = useWorkouts();
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');

  // Функция для генерации названия тренировки
  const generateWorkoutName = (date: string, time: string) => {
    const dateObj = new Date(`${date}T${time}`);
    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();
    
    // Получаем номер тренировки для этой даты
    const workoutsOnSameDate = workouts.filter(workout => {
      const workoutDate = new Date(workout.date);
      return workoutDate.toDateString() === dateObj.toDateString();
    });
    
    const workoutNumber = workoutsOnSameDate.length + 1;
    
    return `Тренировка ${day}-${month}-${year} - ${workoutNumber}`;
  };

  // Получаем дату и время для значений по умолчанию
  const getDateTime = () => {
    const targetDate = preSelectedDate || new Date();
    
    // Форматируем дату в YYYY-MM-DD
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    const date = `${year}-${month}-${day}`;
    
    // Если есть предустановленная дата, используем её время
    if (preSelectedDate) {
      const hours = String(targetDate.getHours()).padStart(2, '0');
      const minutes = String(targetDate.getMinutes()).padStart(2, '0');
      const time = `${hours}:${minutes}`;
      return { date, time };
    }
    
    // Иначе используем текущее время, округленное до часа
    const now = new Date();
    const currentMinutes = now.getMinutes();
    let roundedTime = new Date(now);
    
    if (currentMinutes < 30) {
      // Если до следующего часа меньше 30 минут, округляем назад до текущего часа
      roundedTime.setMinutes(0, 0, 0);
    } else {
      // Если до следующего часа больше или равно 30 минут, округляем вперед до следующего часа
      roundedTime.setHours(roundedTime.getHours() + 1, 0, 0, 0);
    }
    
    const hours = String(roundedTime.getHours()).padStart(2, '0');
    const minutes = String(roundedTime.getMinutes()).padStart(2, '0');
    const time = `${hours}:${minutes}`;
    return { date, time };
  };

  const { date: currentDate, time: currentTime } = getDateTime();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    setValue,
  } = useForm<CreateWorkoutFormData>({
    resolver: zodResolver(createWorkoutSchema),
    mode: 'onChange',
    defaultValues: {
      client_ids: [],
      date: currentDate,
      time: currentTime,
      duration_minutes: 60,
    },
  });

  // Следим за изменениями в форме для валидации
  const watchedValues = watch();
  
  // Синхронизируем selectedClientIds с полем формы
  useEffect(() => {
    setValue('client_ids', selectedClientIds);
  }, [selectedClientIds, setValue]);
  
  // Проверяем, что все обязательные поля заполнены
  const isFormValid = 
    watchedValues.name && 
    watchedValues.date && 
    watchedValues.time && 
    watchedValues.duration_minutes && 
    selectedClientIds.length > 0;



  // Устанавливаем значения по умолчанию при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      const { date, time } = getDateTime();
      setValue('date', date);
      setValue('time', time);
      setValue('duration_minutes', 60);
      
      // Генерируем название тренировки
      const workoutName = generateWorkoutName(date, time);
      setValue('name', workoutName);
      
      // Если есть предзаполненный клиент, добавляем его
      if (preSelectedClient) {
        setSelectedClientIds([preSelectedClient.id]);
      }
    }
  }, [isOpen, setValue, preSelectedClient, workouts, preSelectedDate]);

  // Автоматически обновляем название при изменении даты или времени
  useEffect(() => {
    const watchedDate = watch('date');
    const watchedTime = watch('time');
    
    if (watchedDate && watchedTime && isOpen) {
      const workoutName = generateWorkoutName(watchedDate, watchedTime);
      setValue('name', workoutName);
    }
  }, [watch('date'), watch('time'), isOpen, setValue, workouts]);

  const handleFormSubmit = (data: CreateWorkoutFormData) => {
    console.log('=== FORM SUBMIT STARTED ===');
    console.log('Form submitted with data:', data);
    console.log('Selected client IDs:', selectedClientIds);
    console.log('isFormValid:', isFormValid);
    console.log('isLoading:', isLoading);
    
    // Создаем дату с учетом локального времени
    const [year, month, day] = data.date.split('-').map(Number);
    const [hours, minutes] = data.time.split(':').map(Number);
    
    console.log('Parsed date components:', { year, month, day, hours, minutes });
    
    // Создаем дату в локальном времени
    const dateTime = new Date(year, month - 1, day, hours, minutes, 0, 0);
    console.log('Combined dateTime (local):', dateTime);
    console.log('Combined dateTime (ISO):', dateTime.toISOString());
    console.log('Combined dateTime (local string):', dateTime.toString());
    console.log('Timezone offset:', dateTime.getTimezoneOffset());
    
    // Создаем UTC дату с учетом локального времени
    const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0));
    console.log('UTC dateTime:', utcDate);
    console.log('UTC dateTime (ISO):', utcDate.toISOString());
    
    const workoutData: WorkoutCreate = {
      name: data.name,
      description: data.description,
      date: utcDate.toISOString(),
      duration_minutes: data.duration_minutes,
      notes: data.notes,
      client_ids: selectedClientIds,
      exercises: [], // Пока пустой массив, упражнения будут добавляться на странице тренировки
    };
    
    console.log('Workout data to submit:', workoutData);
    
    onSubmit(workoutData);
    reset();
    setSelectedClientIds([]);
  };

  const handleClose = () => {
    reset();
    setSelectedClientIds([]);
    setClientSearchTerm('');
    setIsClientDropdownOpen(false);
    onClose();
  };

  const handleClientToggle = (clientId: number) => {
    setSelectedClientIds(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleClientRemove = (clientId: number) => {
    setSelectedClientIds(prev => prev.filter(id => id !== clientId));
  };

  // Обработчик выделения текста при фокусе на числовых полях
  const handleInputFocus = useInputFocus();

  // Фильтрация клиентов по поисковому запросу
  const filteredClients = clients.filter(client => {
    const searchLower = clientSearchTerm.toLowerCase();
    return (
      client.first_name.toLowerCase().includes(searchLower) ||
      client.last_name.toLowerCase().includes(searchLower) ||
      client.email?.toLowerCase().includes(searchLower) ||
      client.phone.toLowerCase().includes(searchLower)
    );
  });

  // Получаем выбранных клиентов для отображения
  const selectedClients = clients.filter(client => selectedClientIds.includes(client.id));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Создать новую тренировку
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Форма */}
        <form 
          onSubmit={handleSubmit(handleFormSubmit)}
          className="p-6"
        >
          <div className="space-y-6">
            {/* Название тренировки */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Название тренировки * <span className="text-xs text-gray-500">(заполняется автоматически)</span>
              </label>
              <input
                {...register('name')}
                type="text"
                id="name"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Название будет сгенерировано автоматически"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Описание */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Описание
              </label>
              <textarea
                {...register('description')}
                id="description"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Краткое описание тренировки..."
              />
            </div>

            {/* Дата и время */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                  Дата *
                </label>
                <div className="relative">
                  <input
                    {...register('date')}
                    type="date"
                    id="date"
                    className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.date ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar size={16} className="text-gray-400" />
                  </div>
                </div>
                {errors.date && (
                  <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-2">
                  Время *
                </label>
                <div className="relative">
                  <input
                    {...register('time')}
                    type="time"
                    id="time"
                    className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.time ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Clock size={16} className="text-gray-400" />
                  </div>
                </div>
                {errors.time && (
                  <p className="mt-1 text-sm text-red-600">{errors.time.message}</p>
                )}
              </div>
            </div>

            {/* Длительность */}
            <div>
              <label htmlFor="duration_minutes" className="block text-sm font-medium text-gray-700 mb-2">
                Длительность (минуты) *
              </label>
              <input
                {...register('duration_minutes', { valueAsNumber: true })}
                type="number"
                id="duration_minutes"
                min="1"
                max="480"
                onFocus={handleInputFocus}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.duration_minutes ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="60"
              />
              {errors.duration_minutes && (
                <p className="mt-1 text-sm text-red-600">{errors.duration_minutes.message}</p>
              )}
            </div>

            {/* Скрытое поле для client_ids */}
            <input
              {...register('client_ids')}
              type="hidden"
            />
            
            {/* Выбор клиентов */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Клиенты *
              </label>
              {isClientsLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : clients.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  У вас пока нет клиентов. Сначала добавьте клиентов.
                </div>
              ) : (
                <div className="relative">
                  {/* Выбранные клиенты */}
                  {selectedClients.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {selectedClients.map((client) => (
                        <span
                          key={client.id}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                        >
                          {client.first_name} {client.last_name}
                          <button
                            type="button"
                            onClick={() => handleClientRemove(client.id)}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            <X size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Выпадающий список */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsClientDropdownOpen(!isClientDropdownOpen)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <div className="flex items-center justify-between">
                        <span className={selectedClients.length > 0 ? 'text-gray-900' : 'text-gray-500'}>
                          {selectedClients.length > 0 
                            ? `Выбрано клиентов: ${selectedClients.length}`
                            : 'Выберите клиентов'
                          }
                        </span>
                        <ChevronDown size={16} className="text-gray-400" />
                      </div>
                    </button>

                    {/* Выпадающий список */}
                    {isClientDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                        {/* Поиск */}
                        <div className="p-3 border-b border-gray-200">
                          <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Поиск клиентов..."
                              value={clientSearchTerm}
                              onChange={(e) => setClientSearchTerm(e.target.value)}
                              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>

                        {/* Список клиентов */}
                        <div className="max-h-48 overflow-y-auto">
                          {filteredClients.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              Клиенты не найдены
                            </div>
                          ) : (
                            filteredClients.map((client) => (
                              <button
                                key={client.id}
                                type="button"
                                onClick={() => handleClientToggle(client.id)}
                                className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between ${
                                  selectedClientIds.includes(client.id) ? 'bg-blue-50' : ''
                                }`}
                              >
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {client.first_name} {client.last_name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {client.email}
                                  </div>
                                </div>
                                {selectedClientIds.includes(client.id) && (
                                  <Check size={16} className="text-blue-600" />
                                )}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {selectedClientIds.length === 0 && (
                <p className="mt-1 text-sm text-red-600">Выберите хотя бы одного клиента</p>
              )}
            </div>

            {/* Заметки */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Заметки
              </label>
              <div className="relative">
                <textarea
                  {...register('notes')}
                  id="notes"
                  rows={3}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Дополнительные заметки о тренировке..."
                />
                <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                  <FileText size={16} className="text-gray-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Кнопки */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isLoading}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={!isFormValid || isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Создание...</span>
                </>
              ) : (
                <>
                  <Plus size={16} />
                  <span>Создать тренировку</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateWorkoutModal; 